import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { deleteFile, uploadFile } from "api";
import { createPodBucketAttachment, deletePodBucketAttachment } from "api/services/shipment";
import { PodBucketAttachment, writePodBucketAttachmentsCache } from "api/queries/shipments/useGetPodBucketAttachments";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { ReactComponent as UploadIcon } from "assets/images/iconsV2/upload.svg";
import UploadBar from "components/atoms/UploadBar/UploadBar";
import Button from "components/v2/Button/Button";
import FilePreview from "components/v2/FilePreview/FilePreview";
import errorToast from "helpers/errorToast";
import { classNames, formatFileSize } from "helpers/commonHelpers";
import {
  isSyntheticOtherBucket,
  podBucketAssociatePayload,
} from "components/molecules/TopNav/common/NavIcons/PickupDeliveryProofDialog/podBucketAttachmentHelpers";

import "./PodAttachmentsCard.scss";

const FILE_ACCEPT =
  "application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png";

type AttachmentBucket = {
  id: number;
  bucket_name: string;
  is_mandatory?: boolean;
  isSyntheticOther?: boolean;
};

type PodAttachmentRowProps = {
  bucket: AttachmentBucket;
  dataSource: "pod" | "invoice";
  attachment?: PodBucketAttachment;
  shipmentId: string;
  isReadOnly: boolean;
};

export default function PodAttachmentRow({
  bucket,
  dataSource,
  attachment,
  shipmentId,
  isReadOnly,
}: PodAttachmentRowProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const isBucketMandatory = dataSource === "pod" && bucket.is_mandatory;

  const invalidateAttachments = () => {
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.podBucketAttachments(shipmentId) });
  };

  const handleUploadClick = () => hiddenFileInput.current?.click();

  const handleAssociateAfterUpload = async (fileId: number, reference: string) => {
    try {
      const { data } = await createPodBucketAttachment(shipmentId, podBucketAssociatePayload(fileId, bucket));
      setUploadProgress(0);
      writePodBucketAttachmentsCache(queryClient, shipmentId, (results) => [
        ...results.filter((existing) => existing.file_id !== data.file_id),
        data,
      ]);
      invalidateAttachments();
    } catch (associateError) {
      if (!isSyntheticOtherBucket(bucket)) {
        await deleteFile(reference).catch(() => undefined);
      }
      errorToast(associateError);
      setUploadProgress(0);
    }
  };

  const runUpload = (file: File) => {
    setUploadProgress(1);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);

    uploadFile(formData, {
      onUploadProgress: (progressEvent: ProgressEvent) => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    })
      .then((res) => {
        if (res.status !== 201) {
          throw new Error("Upload failed");
        }
        return handleAssociateAfterUpload(res.data.id, res.data.reference_number);
      })
      .catch((error) => {
        errorToast(error);
        setUploadProgress(0);
      });
  };

  const handleUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      runUpload(file);
    }
  };

  const handleFileDelete = () => {
    if (!attachment?.reference_number) return;
    setIsDeleting(true);
    deletePodBucketAttachment(shipmentId, attachment.reference_number)
      .then((res) => {
        if (res.status === 204) {
          writePodBucketAttachmentsCache(queryClient, shipmentId, (results) =>
            results.filter((existing) => existing.reference_number !== attachment.reference_number),
          );
          invalidateAttachments();
        } else {
          throw new Error(
            intl.formatMessage({
              id: "general.deleteFileError",
              defaultMessage: "Could not delete file, please try again later",
            }),
          );
        }
        setIsDeleting(false);
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "general.deleteFileError",
            defaultMessage: "Could not delete file, please try again later",
          }),
        );
        setIsDeleting(false);
      });
  };

  const showUpload = !isReadOnly && !attachment && !uploadProgress;
  const showUploadBar = uploadProgress > 0;
  const showPreview = Boolean(attachment);

  return (
    <div className="pod-attachments-card__row">
      <p
        className={classNames("pod-attachments-card__bucket-label text-sm font-medium", {
          required: isBucketMandatory,
        })}
      >
        {bucket.bucket_name}
      </p>
      <div className="pod-attachments-card__row-content">
        {showUploadBar && <UploadBar percentCompleted={uploadProgress} />}
        {showPreview && attachment && (
          <FilePreview
            fileName={attachment.filename}
            fileSize={attachment.size == null ? undefined : formatFileSize(attachment.size)}
            url={attachment.url}
            isLoading={isDeleting}
            onClose={isReadOnly ? undefined : handleFileDelete}
          />
        )}
        {showUpload && (
          <Button
            onClick={handleUploadClick}
            className="position-relative pod-attachments-card__upload-btn"
            variant="secondary-color"
            iconLeft={<UploadIcon />}
            label={
              <>
                <FormattedMessage
                  id="tracking.components.uploadFileDialog.uploadedFile"
                  defaultMessage="Upload file"
                />
                <input
                  type="file"
                  accept={FILE_ACCEPT}
                  ref={hiddenFileInput}
                  className="hidden-file-input"
                  onChange={handleUploadChange}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    (e.target as HTMLInputElement).value = "";
                  }}
                />
              </>
            }
          />
        )}
      </div>
    </div>
  );
}
